import { FeatureFlag, ResolverTag } from '@ps/web-core-enums';
import { CashierPath } from '@ps/web-pam-types';

import { getState, select } from '@swf/core/store';
import { encodeQuery } from '@swf/core/utils';

import {
  accountHandler,
  authHandler,
  casinoC2LHandler,
  casinoSOTDHandler,
  gameInternalLaunchHandler,
  gameLaunchHandler,
  launchICEHandler,
  launchWindowHandler,
  modalZoneHandler,
  openWindowForTag,
  pageHandler,
  prhubHandler,
  signupHandler,
  wslC2LRedirect,
  wslHandler
} from './handlers';
import type { HandlerProps, Route } from './matchRoute';

const appRoutes: Route[] = [
  {
    path: '://page/:name',
    handler: pageHandler
  },
  {
    path: '://auth',
    handler: authHandler
  },
  {
    path: '://sign-up',
    handler: signupHandler
  },
  {
    path: '://modal/:zone',
    handler: modalZoneHandler
  },
  {
    path: '://account/(.*)?',
    handler: accountHandler
  },
  {
    path: '://prhub/(.*)?',
    handler: prhubHandler
  },
  {
    path: '://game-launch/:gameTableId(\\d+)',
    handler: gameLaunchHandler
  },
  {
    path: '://casino/casino/:name(\\D\\w+)?/:tab(\\D\\w+)?/:gameTableId(\\d+)?',
    handler: wslHandler
  },
  {
    path: 'launch/:type(game|race)/:id?(\\?)?(.*)',
    handler: casinoC2LHandler
  },
  {
    path: '/casino/launch/:type(game|race)/:id(\\d*)?(\\?)?(.*)',
    handler: gameInternalLaunchHandler
  }
];

const c2lPaths: Route[] = [
  {
    path: '://ice/open',
    handler: ({ path, history }: HandlerProps) => launchICEHandler('challenges', path, history),
    leadToPRHub: true
  },
  {
    path: '://starsrewards/chest',
    handler: ({ history }: HandlerProps) => history.push('/account/my-stars/chest-inventory'),
    leadToPRHub: true
  },
  {
    path: '://menu/ice',
    handler: ({ history }: HandlerProps) => history.push('/account/ice/challenges'),
    leadToPRHub: true
  },
  {
    path: '://menu/pipe/my-stars',
    handler: ({ history }: HandlerProps) => history.push('/account'),
    leadToPRHub: true
  },
  {
    path: '://minigames/ticket/:ticketId/:minigameId/:gameTicketTypeId/:minigameType',
    handler: ({ history, path: { params } }: HandlerProps) =>
      history.push({ pathname: '/account/bonus-game', search: encodeQuery(params) })
  },
  {
    path: '://account/(.*)?',
    handler: accountHandler
  },
  {
    path: '://ice/leaderboards/:id?',
    handler: ({ path, history }: HandlerProps) => launchICEHandler('leaderboards', path, history),
    leadToPRHub: true
  },
  {
    path: '://awardstatus/InstantBonuses',
    handler: ({ history }: HandlerProps) => history.push('/account/my-stars/bonus/instant-bonuses'),
    leadToPRHub: true
  },
  {
    path: '://awardstatus/Bonuses',
    handler: ({ history }: HandlerProps) => history.push('/account/my-stars/bonus/bonuses'),
    leadToPRHub: true
  },
  {
    path: '://awardstatus/MiniGameTickets',
    handler: ({ history }: HandlerProps) => history.push('/account/my-stars/bonus/game-tickets'),
    leadToPRHub: true
  },
  {
    path: '://awardstatus/FreeBets',
    handler: ({ history }: HandlerProps) => {
      if (select.feature.check(getState(), FeatureFlag.ISPMyStarsFreeBets)) {
        history.push('/account/my-stars/bonus/free-bets');
      } else {
        openWindowForTag(ResolverTag.SportsBookFreeBets);
      }
    },
    leadToPRHub: true
  },
  {
    path: '://awardstatus/FreeSpins',
    handler: ({ history }: HandlerProps) =>
      history.push('/account/my-stars/bonus/free-spin-bonuses'),
    leadToPRHub: true
  },
  {
    path: '://awardstatus/SportsCashBonus',
    handler: ({ history }: HandlerProps) =>
      history.push('/account/my-stars/bonus/sports-bonus-cash'),
    leadToPRHub: true
  },
  {
    path: '://menu/cashier/cashout',
    handler: ({ navigate }: HandlerProps) => navigate?.(CashierPath.Withdraw)
  },
  {
    path: '://(cashier/open|menu/lobby/cashier)',
    handler: ({ history }: HandlerProps) => history.push('/account/cashier/home')
  },
  {
    path: '://game-tickets',
    handler: ({ history }: HandlerProps) => history.push('/account/my-stars/bonus/game-tickets'),
    leadToPRHub: true
  },
  {
    path: '://(support|resolve)/:resolverTag',
    handler: launchWindowHandler
  },
  {
    path: '://casino/slots/:subsection(\\D\\w+)?/:tab(\\D\\w+)?',
    handler: wslC2LRedirect(ResolverTag.CasinoSlots)
  },
  {
    path: '://casino/:section((?![slots])\\D\\w+)?/:subsection(\\D\\w+)?/:tab(\\D\\w+)?',
    handler: wslC2LRedirect(ResolverTag.StarsWebCasino)
  },
  {
    path: '://:basepath(sportsisp|sports)(/location)?/:route(.+)?',
    handler: wslC2LRedirect(ResolverTag.StarsWebSports)
  },
  {
    path: '://rewards/sotd',
    handler: casinoSOTDHandler
  }
];

export { c2lPaths, appRoutes };
